<script lang="ts">
  import { fade } from 'svelte/transition';

  import Icon, { isIconVariant } from '$components/ui/icon/icon.svelte';
  import RadioButton from '$components/ui/radio-button.svelte';

  import { t } from '$lib/i18n';
  import { reducedMotion } from '$lib/stores/reduced-motion';

  import type { GetProductQuery } from '$gql/storefront/operations';
  import { getVariantAlertId } from '../alerts';
  import Button from '$components/ui/button.svelte';
  import type { VariantWithBloksStoryblok } from '$types/bloks';
  import { cn } from '$lib/utils';
  import MediaScrollableList from './media-scrollable-list.svelte';

  export let isPreSale: boolean;
  export let product: NonNullable<GetProductQuery['product']>;
  export let wontBeRestocked: boolean;
  export let selectedOptions: Record<string, string>;
  export let sizeGuideOnClick: (() => void) | undefined = undefined;
  export let showStockInfo: boolean | undefined = true;
  export let variantsWithBloks: VariantWithBloksStoryblok[] | undefined = undefined;
  export let variantsAsColors: { variantValue: string; color: string }[] | undefined = undefined;

  const options = product.options;
  const variants = product.variants.nodes;

  $: hasOnlyDefaultVariant = options?.length === 1 && options[0].values.length === 1;
  $: selectedVariant = Object.values(selectedOptions).join(' / ');
  $: selectedProductVariant = variants?.find((variant) => variant.title === selectedVariant);
  $: selectedProductVariantStock = selectedProductVariant?.quantityAvailable || 0;

  $: alert = selectedProductVariant
    ? getVariantAlertId(selectedProductVariant, { isPreSale, wontBeRestocked })
    : undefined;

  const tags = product.tags;
  const isLimitedEdition = tags?.includes('__label:Limited Edition');

  const getCurrentVariantWithBloks = (option: string, value: string) => {
    const v = variantsWithBloks?.find((variant) => variant.variant_name === option);

    if (!v) return;

    const block = v?.blocks?.find((block) => block.value === value);

    return {
      hideDesktop: v.hide_desktop,
      hideMobile: v.hide_mobile,
      media: block?.media,
      infos: v.infos,
      id: block?._uid,
      title: block?.show_variant_title_in_first_media ? value : undefined
    };
  };
</script>

{#if !hasOnlyDefaultVariant}
  <div>
    <div class="flex flex-col gap-6">
      {#each options as option}
        {@const currentVariantWithBloksData = getCurrentVariantWithBloks(
          option.name,
          selectedOptions[option.name]
        )}

        <div>
          <div class="flex gap-2 mb-2 text-sm font-medium text-primary">
            <p>{option.name}</p>
          </div>

          <form class="flex flex-wrap gap-2">
            {#each option.values as value}
              {@const currentVariant = variants?.filter((v) => v.title === value)[0]}
              {@const qnt = currentVariant?.quantityAvailable}
              {@const variantColor = variantsAsColors?.find((v) => v.variantValue === value)?.color}
              {#if variantColor}
                <RadioButton
                  {value}
                  disabled={(isLimitedEdition && qnt === 0) ||
                    (wontBeRestocked && currentVariant?.currentlyNotInStock && qnt === 0)}
                  label={variantColor}
                  id={option.name + value}
                  bind:group={selectedOptions[option.name]}
                  onlyColor={variantColor}
                />
              {:else}
                <RadioButton
                  {value}
                  disabled={(isLimitedEdition && qnt === 0) ||
                    (wontBeRestocked && currentVariant?.currentlyNotInStock && qnt === 0)}
                  label={value}
                  id={option.name + value}
                  bind:group={selectedOptions[option.name]}
                />
              {/if}
            {/each}
          </form>

          {#if currentVariantWithBloksData}
            {#if currentVariantWithBloksData.media}
              {#key currentVariantWithBloksData.id}
                <MediaScrollableList
                  media={currentVariantWithBloksData.media}
                  title={currentVariantWithBloksData.title}
                  class={cn(
                    currentVariantWithBloksData.hideMobile && 'hidden lg:block',
                    currentVariantWithBloksData.hideDesktop && 'lg:hidden'
                  )}
                />
              {/key}
            {/if}
            {#if currentVariantWithBloksData.infos}
              <div
                class={cn(
                  'flex flex-col gap-2.5 mt-2',
                  currentVariantWithBloksData.hideMobile && 'hidden lg:block',
                  currentVariantWithBloksData.hideDesktop && 'lg:hidden'
                )}
              >
                {#each currentVariantWithBloksData.infos as info}
                  {@const icon = info.icon?.toString()}
                  <p class="text-sm text-primary/60 flex items-center gap-x-1">
                    {#if icon && isIconVariant(icon)}
                      <Icon {icon} size="md" />
                    {/if}
                    <span class={cn('block text-sm')}>{info.label}</span>
                  </p>
                {/each}
              </div>
            {/if}
          {/if}
        </div>
      {/each}
    </div>
    {#if sizeGuideOnClick}
      <Button
        asChild
        variant="ghost"
        class="mt-4"
        size="xs"
        icon="ruler"
        on:click={sizeGuideOnClick}
      >
        {$t('pdp.variants.drawer-title')}</Button
      >
    {/if}
    {#if showStockInfo && alert}
      <p class="mt-6 text-sm/none text-primary/80 h-5">
        <span
          class="flex items-center gap-1"
          transition:fade={{ duration: $reducedMotion ? 0 : 100 }}
        >
          <Icon size="md" icon="alert-hexagon-bold" />
          {$t(alert, {
            count: selectedProductVariantStock.toString()
          })}
        </span>
      </p>
    {/if}
  </div>
{/if}
